import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, Eyebrow, Lede, SectionTitle } from '../AumhumSite/sections'
import { CheckCircle2, ChevronDown } from '../AumhumSite/icons'
import {
  CONCERNS_BY_PATHWAY,
  DURATION_OPTIONS,
  PRIOR_SUPPORT_OPTIONS,
  SUBJECT_TYPE_OPTIONS,
  chipsForPathway,
} from './intakeChips'
import {
  deleteProspectReport,
  updateProspectIntake,
  uploadProspectReport,
} from 'src/consumer/services/patientProspectService'
import './CareStatusIntake.css'

// Top-level progressive intake form for /care/status. Each section is
// collapsible and sees its slice of the form state. Saves are debounced
// at the form level (one PATCH /api/secure/me/patient-prospect/intake
// covers any subset of fields).
//
// Reports upload is its own section with multipart POST per-file and a
// list of already-uploaded reports.

const SAVE_DEBOUNCE_MS = 900

function formStateFromBundle(bundle) {
  const p = bundle?.prospect || {}
  const a = p.intakeAnswers || {}
  const subj = a.subject || {}
  return {
    presentingConcerns: Array.isArray(a.presentingConcerns) ? a.presentingConcerns : [],
    freeText: a.freeText || '',
    duration: a.duration || null,
    priorSupportHistory: a.priorSupportHistory || null,
    subjectType: p.subjectType || 'SELF',
    subjectName: subj.name || '',
    subjectDob: subj.dob || '',
    subjectGender: subj.gender || '',
    medicalHistorySummary: a.medicalHistorySummary || '',
    preferredClinicHint: p.preferredClinicHint || a.preferredClinicHint || '',
  }
}

function payloadFromState(state) {
  const subjectType = state.subjectType || 'SELF'
  const subject =
    subjectType !== 'SELF' && (state.subjectName || state.subjectDob || state.subjectGender)
      ? {
          name: state.subjectName || null,
          dob: state.subjectDob || null,
          gender: state.subjectGender || null,
        }
      : null
  return {
    presentingConcerns: state.presentingConcerns,
    freeText: state.freeText,
    duration: state.duration,
    priorSupportHistory: state.priorSupportHistory,
    subjectType,
    subject,
    medicalHistorySummary: state.medicalHistorySummary,
    preferredClinicHint: state.preferredClinicHint,
  }
}

function Section({ title, summary, isFilled, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={`csi-section${open ? ' is-open' : ''}`}>
      <button type="button" className="csi-section-header" onClick={() => setOpen((o) => !o)}>
        <span>
          <span className="csi-section-title">{title}</span>
          {summary ? <span className="csi-section-summary">{summary}</span> : null}
        </span>
        <span className={`csi-section-status${isFilled ? ' is-filled' : ''}`} aria-hidden>
          {isFilled ? <><CheckCircle2 /> Saved</> : 'Not yet'}
        </span>
        <span className="csi-section-chev" aria-hidden><ChevronDown /></span>
      </button>
      {open ? <div className="csi-section-body">{children}</div> : null}
    </div>
  )
}

function ChipGroup({ options, value, onChange, multi = true }) {
  const selectedSet = useMemo(() => new Set(Array.isArray(value) ? value : []), [value])
  return (
    <div className="csi-chips" role={multi ? 'group' : 'radiogroup'}>
      {options.map((opt) => {
        const active = multi ? selectedSet.has(opt.id) : value === opt.id
        return (
          <button
            key={opt.id}
            type="button"
            className={`csi-chip${active ? ' is-active' : ''}`}
            onClick={() => {
              if (multi) {
                const next = new Set(selectedSet)
                if (next.has(opt.id)) next.delete(opt.id)
                else next.add(opt.id)
                onChange(Array.from(next))
              } else {
                onChange(opt.id)
              }
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

function RadioCards({ options, value, onChange }) {
  return (
    <div className="csi-radio-grid" role="radiogroup">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          role="radio"
          aria-checked={value === opt.id}
          className={`csi-radio${value === opt.id ? ' is-active' : ''}`}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function ReportsSection({ bundle, onUpdated }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef(null)
  const reports = bundle?.prospect?.reports || []

  const onFiles = async (files) => {
    setError('')
    if (!files || files.length === 0) return
    const file = files[0] // upload one at a time; keeps UX honest about progress
    if (file.size > 25 * 1024 * 1024) {
      setError('File is too large. Max 25 MB.')
      return
    }
    if (!/^(application\/pdf|image\/(jpe?g|png))$/i.test(file.type)) {
      setError('Only PDF, JPG, or PNG files are accepted.')
      return
    }
    try {
      setBusy(true)
      const updatedProspect = await uploadProspectReport(file, null)
      // Update bundle locally with the freshly returned prospect.
      onUpdated((prev) => ({
        ...(prev || {}),
        prospect: updatedProspect || prev?.prospect,
      }))
    } catch (e) {
      setError(e.message || 'Could not upload that file.')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const onRemove = async (reportId) => {
    setError('')
    try {
      const updatedProspect = await deleteProspectReport(reportId)
      onUpdated((prev) => ({
        ...(prev || {}),
        prospect: updatedProspect || prev?.prospect,
      }))
    } catch (e) {
      setError(e.message || 'Could not remove that file.')
    }
  }

  return (
    <Section
      title="Reports & documents"
      summary="Upload past reports, prescriptions, or scans you'd like the specialist to see."
      isFilled={reports.length > 0}
    >
      <div
        className={`csi-dropzone${dragging ? ' is-dragging' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files) }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          style={{ display: 'none' }}
          onChange={(e) => onFiles(e.target.files)}
        />
        {busy ? (
          <strong>Uploading…</strong>
        ) : (
          <>
            <strong>Drag a file here, or click to browse</strong>
            <span className="csi-dropzone-hint">
              PDF, JPG, or PNG. Up to 25 MB each, 10 files total.
            </span>
          </>
        )}
      </div>

      {error ? <div className="csi-help" style={{ color: '#b42318' }}>{error}</div> : null}

      {reports.length > 0 ? (
        <ul className="csi-reports-list">
          {reports.map((r) => (
            <li key={r.id} className="csi-report">
              <div className="csi-report-meta">
                <span className="csi-report-name">{r.fileName || 'Report'}</span>
                <span className="csi-report-sub">
                  {r.contentType || 'file'}
                  {r.sizeBytes ? ` · ${(r.sizeBytes / 1024).toFixed(0)} KB` : ''}
                  {r.uploadedAt ? ` · ${new Date(r.uploadedAt).toLocaleDateString()}` : ''}
                </span>
              </div>
              <button type="button" className="csi-report-remove" onClick={() => onRemove(r.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  )
}

export default function CareStatusIntakeForm({ bundle, onBundleChange }) {
  const initial = useMemo(() => formStateFromBundle(bundle), [bundle])
  const [state, setState] = useState(initial)
  const [saveStatus, setSaveStatus] = useState('idle') // idle | saving | saved | error
  const [saveError, setSaveError] = useState('')
  const debounceRef = useRef(null)
  const isMountedRef = useRef(false)

  // Reset state if the bundle replaces (e.g. server fetch returned new data).
  useEffect(() => {
    setState(formStateFromBundle(bundle))
  }, [bundle?.prospect?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return undefined
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSaveStatus('saving')
    debounceRef.current = setTimeout(async () => {
      try {
        const refreshed = await updateProspectIntake(payloadFromState(state))
        if (refreshed && onBundleChange) {
          onBundleChange(refreshed)
        }
        setSaveStatus('saved')
        setSaveError('')
      } catch (e) {
        setSaveStatus('error')
        setSaveError(e.message || 'Could not save your changes.')
      }
    }, SAVE_DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // We deliberately depend only on `state` — onBundleChange is stable per
    // parent render but a stale closure is fine because we re-fetch bundle.
  }, [state]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (value) => setState((s) => ({ ...s, [key]: value }))

  const pathway = bundle?.prospect?.pathway
  const concernChips = chipsForPathway(pathway)

  const concernSummary = state.presentingConcerns.length
    ? state.presentingConcerns
        .map((id) => concernChips.find((c) => c.id === id)?.label || id)
        .join(', ')
    : ''

  const subjectSummary = (() => {
    if (state.subjectType === 'SELF') return 'For me'
    const opt = SUBJECT_TYPE_OPTIONS.find((o) => o.id === state.subjectType)
    if (state.subjectName) return `${opt?.label || state.subjectType} — ${state.subjectName}`
    return opt?.label || state.subjectType
  })()

  return (
    <div>
      <Card className="pps-card-form" emphasised>
        <Eyebrow>Help us help you better</Eyebrow>
        <SectionTitle as="h2">Tell us more</SectionTitle>
        <Lede>
          Each section saves on its own. Share what you can — you can always come back to
          add more.
        </Lede>

        <div className={`csi-savebar is-${saveStatus}`} aria-live="polite">
          {saveStatus === 'saving' ? 'Saving…' : null}
          {saveStatus === 'saved' ? <><CheckCircle2 /> Saved</> : null}
          {saveStatus === 'error' ? `Could not save — ${saveError}` : null}
        </div>

        <Section
          title="What's bringing you here"
          summary={concernSummary || state.freeText
            ? (concernSummary || state.freeText.slice(0, 80))
            : "Pick what fits, or tell us in your own words."}
          isFilled={state.presentingConcerns.length > 0 || !!state.freeText}
          defaultOpen
        >
          <ChipGroup
            options={concernChips}
            value={state.presentingConcerns}
            onChange={set('presentingConcerns')}
          />
          <textarea
            className="csi-textarea"
            placeholder="In your own words — what's been going on?"
            value={state.freeText}
            onChange={(e) => set('freeText')(e.target.value)}
            style={{ marginTop: '0.75rem' }}
          />
        </Section>

        <Section
          title="How long has this been going on"
          summary={DURATION_OPTIONS.find((d) => d.id === state.duration)?.label || ''}
          isFilled={!!state.duration}
        >
          <RadioCards options={DURATION_OPTIONS} value={state.duration} onChange={set('duration')} />
        </Section>

        <Section
          title="What you've tried so far"
          summary={PRIOR_SUPPORT_OPTIONS.find((d) => d.id === state.priorSupportHistory)?.label || ''}
          isFilled={!!state.priorSupportHistory}
        >
          <RadioCards
            options={PRIOR_SUPPORT_OPTIONS}
            value={state.priorSupportHistory}
            onChange={set('priorSupportHistory')}
          />
        </Section>

        <Section
          title="Who is this for"
          summary={subjectSummary}
          isFilled={state.subjectType !== 'SELF'}
        >
          <RadioCards
            options={SUBJECT_TYPE_OPTIONS}
            value={state.subjectType}
            onChange={(v) => set('subjectType')(v)}
          />
          {state.subjectType !== 'SELF' ? (
            <div className="csi-field-grid">
              <input
                className="csi-input"
                type="text"
                placeholder="Their name"
                value={state.subjectName}
                onChange={(e) => set('subjectName')(e.target.value)}
              />
              <input
                className="csi-input"
                type="date"
                placeholder="Date of birth"
                value={state.subjectDob}
                onChange={(e) => set('subjectDob')(e.target.value)}
              />
              <input
                className="csi-input"
                type="text"
                placeholder="Gender (optional)"
                value={state.subjectGender}
                onChange={(e) => set('subjectGender')(e.target.value)}
              />
            </div>
          ) : null}
        </Section>

        <Section
          title="Any relevant medical history"
          summary={state.medicalHistorySummary
            ? state.medicalHistorySummary.slice(0, 80) + (state.medicalHistorySummary.length > 80 ? '…' : '')
            : ''}
          isFilled={!!state.medicalHistorySummary}
        >
          <textarea
            className="csi-textarea"
            placeholder="Anything that might help the specialist understand the full picture — past diagnoses, medications, allergies, etc."
            value={state.medicalHistorySummary}
            onChange={(e) => set('medicalHistorySummary')(e.target.value)}
          />
        </Section>

        <ReportsSection bundle={bundle} onUpdated={onBundleChange} />

        <Section
          title="Any clinic in mind"
          summary={state.preferredClinicHint
            ? state.preferredClinicHint.slice(0, 80)
            : "Optional — type a name, area, or 'open to anywhere'."}
          isFilled={!!state.preferredClinicHint}
        >
          <input
            className="csi-input"
            type="text"
            placeholder="e.g. Bangalore — south, in-person if possible"
            value={state.preferredClinicHint}
            onChange={(e) => set('preferredClinicHint')(e.target.value)}
          />
          <div className="csi-help">
            We won&rsquo;t lock you in — this is just a hint for our care team.
          </div>
        </Section>
      </Card>
    </div>
  )
}

// CONCERNS_BY_PATHWAY is re-exported here for tests / debugging convenience.
export { CONCERNS_BY_PATHWAY }
